import { getFirestore, doc, runTransaction, collection, query, where, getDocs, orderBy, limit, serverTimestamp, getDoc, updateDoc, increment } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { InAppNotificationService } from "./in-app-notification.service.js";

const db = getFirestore(app);

export const PointService = {
    /**
     * Generate a unique referral code
     * Format: 6 characters (Alphanumeric uppercase), e.g., "A8F3Z9"
     */
    generateReferralCode: () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    },

    /**
     * Get Point Transaction History for User
     * @param {string} userId 
     * @param {number} limitCount 
     */
    getHistory: async (userId, limitCount = 20, lastDoc = null) => {
        try {
            // [STANDARD] V2 Schema uses 'userId' (CamelCase)
            let q = query(
                collection(db, "point_transactions"),
                where("userId", "==", userId),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );

            if (lastDoc) {
                const { startAfter } = await import("firebase/firestore");
                q = query(q, startAfter(lastDoc));
            }

            const snapshot = await getDocs(q);
            return {
                items: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                lastVisible: snapshot.docs[snapshot.docs.length - 1]
            };
        } catch (error) {
            console.error("PointService.getHistory Error:", error);
            return { items: [], lastVisible: null };
        }
    },

    /**
     * Earn Points (Atomic Transaction)
     * @param {string} userId - Target User ID
     * @param {number} amount - Amount to earn (Positive Integer)
     * @param {string} category - 'attendance' | 'referral' | 'comment' | 'like' | 'rating' | 'completion' | 'admin'
     * @param {string} description - Description for UI
     * @param {string|null} referenceId - Related Series ID, Comment ID, or Referrer ID
     * @param {string} currency - 'activity' (default) or 'star'
     */
    earnPoints: async (userId, amount, category, description, referenceId = null, currency = 'activity') => {
        try {
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);

            // [SECURE] Categorized Routing
            if (category === 'attendance') {
                const earnDaily = httpsCallable(functions, 'earnDailyLoginReward');
                await earnDaily();
                return;
            }

            if (category === 'welcome') {
                const earnWelcome = httpsCallable(functions, 'earnWelcomeReward');
                await earnWelcome();
                return;
            }

            // [GENERIC] Use new earnActivityPoint for Reading, Share, Completion etc.
            if (category === 'reading_reward' || category === 'reading' || category === 'share' || category === 'completion') {
                const earnFn = httpsCallable(functions, 'earnActivityPoint');
                // Map legacy category 'reading_reward' -> 'reading_reward'
                await earnFn({ category: 'reading_reward', referenceId });
                return;
            }

            // Note: 'comment' and 'like' are handled by server triggers/actions ONLY.
            // Client should not call earnPoints for them.
            if (category === 'comment' || category === 'like') {
                console.log("[PointService] Points for comment/like are handled by server triggers automatically.");
                return;
            }

            // For Admin/Other, rely on specifics or fail.
            console.warn(`[PointService] Unhandled category: ${category}`);

        } catch (error) {
            // [FIX] Handle Idempotency (Already Earned)
            // Firebase Functions throws with error.message containing the failure reason
            if (error.message && (error.message.includes("이미 출석체크") || error.message.includes("Already"))) {
                console.warn("[PointService] 금일 출석체크가 이미 완료되었습니다.");
                return;
            }
            console.error("PointService.earnPoints Error:", error);
        }
    },

    /**
     * Unlock Content (Rent or Own) - Server Side
     * @param {string} seriesId 
     * @param {string} episodeId 
     * @param {string} type - 'rent' | 'own'
     * @param {number} price 
     * @param {string} itemName 
     */
    unlockContent: async (seriesId, episodeId, type, price, itemName) => {
        try {
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const purchaseContent = httpsCallable(functions, 'purchaseContent');

            const result = await purchaseContent({
                seriesId,
                episodeId,
                type,
                price,
                itemName
            });

            return result.data;
        } catch (error) {
            console.error("PointService.unlockContent Error:", error);
            throw error;
        }
    },

    /**
     * Use Points (Atomic Transaction) - Legacy/Generic
     * Logic: Deduct from Activity Points first. If insufficient, deduct from Star Points.
     * @param {string} userId 
     * @param {number} amount - Amount to use (Positive Integer)
     * @param {string} category - 'unlock_episode' | 'item_purchase' | 'support'
     * @param {string} description 
     * @param {string|null} referenceId 
     */
    usePoints: async (userId, amount, category, description, referenceId = null) => {
        if (amount <= 0) throw new Error("Amount must be positive");

        try {
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const usePointTransaction = httpsCallable(functions, 'usePointTransaction');

            await usePointTransaction({
                amount,
                category,
                description,
                referenceId
            });

            return true;
        } catch (error) {
            console.error("PointService.usePoints Error:", error);
            throw error; // Let caller handle "Error"
        }
    },

    processReferral: async (newUserId, referrerCode) => {
        if (!referrerCode) return;

        try {
            // [SECURE] Server-Side Referral Processing
            // Client cannot query 'users' by referralCode due to security rules.
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const processReferralFn = httpsCallable(functions, 'processReferral');

            await processReferralFn({ referrerCode });

        } catch (error) {
            console.error("PointService.processReferral Error:", error);
        }
    },

    /**
     * Helper for Reading Rewards
     * @param {string} seriesId - Series ID for dedup
     * @param {string} episodeId - Episode ID for dedup
     */
    earnReadingReward: async (seriesId, episodeId) => {
        const uid = window.user?.uid;
        if (!uid) return;
        const referenceId = (seriesId && episodeId) ? `${seriesId}_${episodeId}` : null;
        return PointService.earnPoints(uid, 1, 'reading_reward', 'point.reason.reading', referenceId);
    },

    /**
     * [FALLBACK] Client-Side Logging
     * Use only when Server Function is unreachable.
     */
    // [REMOVED] Client-side logging is insecure and deprecated. Use Server Actions.
};
